import { useQuery } from "@tanstack/react-query";
import egam from "../../services/egam";
import { getClientData } from "../../services/auth";
import { queryKeys } from "../api/queryKeys";
import { numberFormat } from "../../utils/numberFormat";
import UfKit from "../../types/UfKit";

interface KitProduct {
  codigo: string;
  nome: string;
  quantidade: string;
  desconto: string;
  preco: string;
  formattedDesconto: string;
  formattedPreco: string;
}

interface KitProductsParams {
  ufKit: UfKit[];
}

export const useKitProducts = (
  params: KitProductsParams | null,
  enabled: boolean = true
) => {
  return useQuery<KitProduct[]>({
    queryKey: queryKeys.kitProducts(params),
    queryFn: async () => {
      if (!params || !params.ufKit || params.ufKit.length === 0) {
        throw new Error("UF Kit não fornecido");
      }

      const cliente = getClientData();
      if (!cliente) {
        throw new Error("Cliente não autenticado");
      }

      const formData = new FormData();
      formData.append("cliente", String(cliente.cd_cliente));

      let kit = params.ufKit.find(kitUf => kitUf.uf === cliente.uf);

      // Lógica especial para cliente 16684
      if (cliente.cd_cliente === 16684) {
        if (!kit) {
          [kit] = params.ufKit;
        }
      }

      if (!kit) {
        throw new Error("Kit não encontrado para a UF do cliente");
      }

      const response = await egam.post<{ data?: KitProduct[] } | KitProduct[]>(
        `lista-produtos-kit/${kit.cd_kit}`,
        formData,
        {
          headers: {
            "content-type": "multipart/form-data",
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Problema ao buscar itens");
      }

      // A resposta pode vir como array direto ou como objeto com propriedade data
      const productsData = Array.isArray(response.data)
        ? response.data
        : (response.data as { data?: KitProduct[] })?.data || [];

      // Formata os produtos como o Redux fazia
      return productsData.map(product => ({
        ...product,
        formattedPreco: numberFormat(parseFloat(product.preco)),
        formattedDesconto: `${numberFormat(parseFloat(product.desconto))}%`,
      }));
    },
    enabled:
      enabled &&
      params !== null &&
      params.ufKit !== undefined &&
      params.ufKit.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};
